import { z } from "zod";

// TES-380 — "Min resa": the patient-facing journey timeline.
// GET /patient-api/v1/journey-timeline returns the patient's OWN milestones,
// newest first, locale-resolved server-side (sv is the approved base, en per
// preferredLocale) so the app renders plain strings directly.
//
// Five read-time producers feed it (no new tables, no schema change):
//   1. the latest intake FormSubmission        → 'intake_completed'
//   2. DomainEvents (treatment_activated, cycle_renewed, phase_changed,
//      lab_results_ready)                      → the event key as type
//   3. milestone FormSubmissions               → 'milestone'
//   4. COMPLETED FlowAssignments whose snapshotted authorMetadata carries
//      min_resa_publishable === true           → authorMetadata's
//      min_resa_entry_type (free string, fallback 'moment')
//   5. non-cancelled Meetings                  → 'meeting_booked' (TES-790)
//
// `type` is deliberately an open string: producer 4 promotes the authoring
// side's `min_resa_entry_type` verbatim (TES-370 keeps authorMetadata an
// opaque passthrough), so pinning an enum here would break the moment Drive
// adds a new entry type. Known values today: intake_completed,
// treatment_activated, cycle_renewed, phase_changed, lab_results_ready,
// milestone, moment, doctor_followup (TES-486 — the doctor's clinical
// follow-up from the treatment cycle; emitted only once its Ö-43 copy exists),
// meeting_booked (TES-790).
//
// TES-790 — an open `type` is NOT an open `title`. No producer may put an
// internal key in front of a patient: no moment id (CP-xx), flow key
// (F-01/F-02/F-07), daily-tip id (DT-xx), authoring label or status enum. A
// producer with no patient copy for an entry drops the entry; it never
// backfills the title from an internal string. Producer 4 learned this the
// hard way — it published `Flow.name` and a patient read
// "CP-01 – Boka vårdmöte".
//
// TES-293 patient-disclosure rule: entries are built ONLY from the patient's
// own answers and care events — never from flags/hardStops/review metadata.
// TES-390 §8 (the MVP) — every entry carries ONE of three categories so
// Min resa reads as a relational memory, not an admin log:
//   care_event — care events (treatment started, lab results ready, …)
//   milestone  — milestones (goal weight reached, phase transitions, month
//                done, …)
//   insight    — insights, ONLY from things the patient actively answered,
//                registered or got professional feedback on (F-01/F-02,
//                check-ins, monthly follow-ups, coach/doctor feedback) —
//                never passive usage or article clicks.
export const JOURNEY_TIMELINE_CATEGORIES = [
  "care_event",
  "milestone",
  "insight",
] as const;
export const journeyTimelineCategorySchema = z.enum(
  JOURNEY_TIMELINE_CATEGORIES,
);
export type JourneyTimelineCategory = z.infer<
  typeof journeyTimelineCategorySchema
>;

// Category from the (open) entry type. Server-side producers use this so the
// mapping has one home; unknown/authored types default to care_event.
export function journeyTimelineCategoryOf(
  type: string,
): JourneyTimelineCategory {
  switch (type) {
    case "milestone":
    case "weight_goal_reached":
    case "phase_changed":
      return "milestone";
    case "intake_completed":
    case "f01_summary":
    // A PatientReflection is the patient actively answering an authored
    // prompt, which is exactly what §8 reserves `insight` for — unlike the
    // article click that led there, which stays off the timeline entirely.
    // NB `patient_reflection`, not `reflection`: the latter is already taken
    // by an AUTHORED min_resa_entry_type on flow packages (a content label
    // chosen by editors), and the two are different things.
    case "patient_reflection":
      return "insight";
    // TES-790 — booking the consultation is a care event, like the
    // treatment starting or the doctor's decision landing. Spelled out rather
    // than left to the default: a patient-visible category is a product
    // decision, and it should not silently change if the fallback ever does.
    case "meeting_booked":
      return "care_event";
    default:
      return "care_event";
  }
}

export const journeyTimelineEntrySchema = z.object({
  id: z.string(),
  type: z.string().min(1),
  category: journeyTimelineCategorySchema,
  // ISO-8601 timestamp of the underlying moment (submission, event, or
  // package completion).
  occurredAt: z.string(),
  title: z.string(),
  body: z.string().nullable(),
});
export type JourneyTimelineEntry = z.infer<typeof journeyTimelineEntrySchema>;

export const journeyTimelineResponseSchema = z.object({
  entries: z.array(journeyTimelineEntrySchema),
});
export type JourneyTimelineResponse = z.infer<
  typeof journeyTimelineResponseSchema
>;
